import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type AnyPalette,
  type UserPalette,
  useDeletePalette,
  usePaletteLibrary,
  useSetFavoriteColor,
} from '@/entities/palette';
import { useErrorMessage } from '@/shared/i18n';
import {
  type ColorLibraryProps,
  ConfirmDialog,
  HelpTip,
  Icons,
  Popover,
  useToast,
} from '@/shared/ui';

import styles from './ColorPalettes.module.css';
import { type EditorSeed, PaletteEditor } from './PaletteEditor';
import { LibraryPanel } from './LibraryPanel';
import { usePaletteNames } from './names';

/**
 * What the shared `ColorPicker` shows beside its own swatches and HEX field: the favorite colors
 * (one tap), a star that adds / removes the current color, and the "Palettes" button that opens
 * the library. The picker's native editor, presets and validation are untouched; choosing a
 * library color is `onPick(color)`, the same call a preset swatch makes.
 */
export function ColorTools({
  label,
  value,
  workspaceId,
  disabled = false,
  onPick,
}: ColorLibraryProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const message = useErrorMessage();
  const { nameOf } = usePaletteNames();
  const library = usePaletteLibrary();
  const setFavorite = useSetFavoriteColor();
  const remove = useDeletePalette();
  const [editor, setEditor] = useState<EditorSeed | null>(null);
  const [deleting, setDeleting] = useState<UserPalette | null>(null);

  const favorites = library.data?.favorite_colors ?? [];
  const current = value?.toLowerCase() ?? null;
  const isFavorite = current !== null && favorites.includes(current);
  const workspaces = library.data?.workspaces ?? [];

  const duplicate = (palette: AnyPalette) => {
    setEditor({
      name: t('palettes.copyName', { name: nameOf(palette) }),
      colors: palette.colors,
      workspaceId: null,
    });
  };

  return (
    <div className={styles.tools}>
      {favorites.length > 0 && (
        <div
          className={styles.favorites}
          role="group"
          aria-label={`${label}: ${t('palettes.favoriteColors')}`}
        >
          {favorites.map((color) => (
            <button
              key={color}
              type="button"
              className={`${styles.chip} ${styles.chipButton}`}
              style={{ background: color }}
              disabled={disabled}
              aria-label={t('palettes.useColor', { color })}
              title={color}
              onClick={() => {
                onPick(color);
              }}
            />
          ))}
        </div>
      )}
      <div className={styles.toolRow}>
        <Popover
          label={t('palettes.library')}
          width={380}
          trigger={(props) => (
            <button type="button" {...props} className={styles.toolButton} disabled={disabled}>
              <Icons.palette /> {t('palettes.open')}
            </button>
          )}
        >
          {(close) => (
            <LibraryPanel
              library={library.data}
              loading={library.isPending}
              workspaceId={workspaceId}
              actions={{
                onEdit: (palette) => {
                  close();
                  setEditor({ palette, name: palette.name, colors: palette.colors });
                },
                onDuplicate: (palette) => {
                  close();
                  duplicate(palette);
                },
                onDelete: (palette) => {
                  close();
                  setDeleting(palette);
                },
              }}
              onPick={(color) => {
                close();
                onPick(color);
              }}
              onCreate={() => {
                close();
                setEditor({
                  name: '',
                  colors: current ? [current] : [],
                  workspaceId: null,
                });
              }}
            />
          )}
        </Popover>
        {current && (
          <button
            type="button"
            className={styles.iconButton}
            disabled={disabled}
            aria-pressed={isFavorite}
            aria-label={t(isFavorite ? 'palettes.removeFavorite' : 'palettes.addFavorite', {
              color: current,
            })}
            title={t(isFavorite ? 'palettes.removeFavorite' : 'palettes.addFavorite', {
              color: current,
            })}
            onClick={() => {
              setFavorite.mutate({ color: current, favorite: !isFavorite });
            }}
          >
            <span className={isFavorite ? styles.starOn : undefined}>
              <Icons.star />
            </span>
          </button>
        )}
        <HelpTip topic={t('palettes.open')} topicId="color-palettes" />
      </div>
      {editor && (
        <PaletteEditor
          seed={editor}
          workspaces={workspaces}
          onClose={() => {
            setEditor(null);
          }}
        />
      )}
      {deleting && (
        <ConfirmDialog
          title={t('palettes.deleteTitle')}
          message={t('palettes.deleteMessage', { name: deleting.name })}
          confirmLabel={t('palettes.delete')}
          pending={remove.isPending}
          onCancel={() => {
            setDeleting(null);
          }}
          onConfirm={() => {
            remove.mutate(deleting.id, {
              onSettled: () => {
                setDeleting(null);
              },
              onError: (error) => {
                toast.show({ message: message(error) });
              },
            });
          }}
        />
      )}
    </div>
  );
}
